/**
 * MaestroWorkflowStepper
 *
 * Purpose: Compact horizontal progress rail for the Maestro panel's workflow
 *   steps (ORCH-277). Replaces the vertical accordion of mostly-locked rows:
 *   one header row — "Maestro · Step N: Name Agent" — with the
 *   dot-and-connecting-line rail directly beneath it. Only the viewed step's
 *   content is expanded — and that content is rendered by the parent, not here.
 *
 * Header row (ORCH-426): the panel title and the viewed step's name are one
 *   row, above the dots. This reverses ORCH-401, which put the rail first and
 *   the step name beneath it and recommended that order; ORCH-426 merged the
 *   separate "Maestro" title row into the step heading, which saves a row and
 *   names the agent the user is working with. The agent label is derived at
 *   render — the step's label plus "Agent" — not stored; a step whose agent
 *   needs a different name is a Workflow Manager field, deferred by the
 *   ticket. The title is a prop so this component stays presentational. A
 *   long row truncates with a tooltip rather than wrapping, and the rail sits
 *   8px beneath it so the two read as one header group; the parent leaves a
 *   larger gap (24px) before the step content.
 *
 * Architecture: Presentational and controlled, in the same pattern
 *   MaestroMappingsStepper established — the parent owns which step is viewed
 *   and passes it in. Dot state is derived from the `completed` / `locked` flags
 *   the caller supplies (from `PhaseSection`); this component never computes
 *   lifecycle state and knows nothing about engine phases, workflow ids, or
 *   what any particular step's content happens to be.
 *
 * Step labels and ordering come from the caller's workflow steps, so a step
 * added in the Workflow Manager renders a dot with no change here.
 *
 * Discoverability (ORCH-407): every dot has a tooltip reading "Step N: Name"
 *   (on hover, keyboard focus and tap, with no enter delay), and its
 *   `aria-label` is the same text. The number is the step's position in the
 *   workflow; no status word is shown. The "Step X of N" counter, the "Next:"
 *   hint and the step list that opened from the counter were all removed at
 *   Biz Review — the tooltips are the whole discoverability story, so the
 *   header row carries only the title and the viewed step's "Step N: Name
 *   Agent".
 *
 * Dependencies: MUI Box/Typography/Tooltip, theme primary/divider tokens.
 */

import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

/** One dot on the rail: a workflow step's identity plus its lifecycle state. */
export interface WorkflowStepperItem {
  /** Section key — the workflow `step_id`. */
  key: string;
  /** The Workflow Manager step's own label. */
  label: string;
  completed: boolean;
  locked: boolean;
}

export interface MaestroWorkflowStepperProps {
  /** Steps in sequence order, one dot each. */
  steps: WorkflowStepperItem[];
  /** The panel's title, leading the header row in bold. Defaults to "Maestro". */
  title?: string;
  /** Index of the step whose name heads the panel and whose content is expanded. */
  activeIndex: number;
  /**
   * Index the engine is currently running, or -1. Pulses that dot so an
   * in-progress step stays identifiable while the user reviews a finished one.
   */
  runningIndex?: number;
  /** Select a reachable (unlocked) step to view. Omit to make the rail inert. */
  onStepSelect?: (index: number) => void;
}

/**
 * `upcoming` is a reachable step that is neither complete, running nor viewed.
 * Before ORCH-407 it fell through to `locked`. The state drives the dot's look
 * and its `data-state` attribute only; it is not spoken or shown as text.
 */
type DotState = 'complete' | 'active' | 'running' | 'upcoming' | 'locked';

const dotState = (
  step: WorkflowStepperItem,
  idx: number,
  viewedIndex: number,
  runningIndex: number
): DotState => {
  if (step.completed) return 'complete';
  if (step.locked) return 'locked';
  if (idx === runningIndex) return 'running';
  if (idx === viewedIndex) return 'active';
  return 'upcoming';
};

/** Hollow dots: steps the user has not reached yet. */
const isHollow = (state: DotState): boolean => state === 'locked' || state === 'upcoming';

/**
 * The one text a dot carries, as tooltip and accessible name alike. The number
 * is the step's position in the workflow, so a step added or renamed in the
 * Workflow Manager renumbers itself (ORCH-407 Biz Review wording).
 */
const stepName = (step: WorkflowStepperItem, idx: number): string =>
  `Step ${idx + 1}: ${step.label}`;

/**
 * The step half of the header row: "Step N: Name Agent". The agent name is the
 * workflow step's label plus "Agent" (ORCH-426) — derived here, never stored.
 */
export const stepAgentName = (step: WorkflowStepperItem, idx: number): string =>
  `${stepName(step, idx)} Agent`;

/** Separator between the title and the step half of the header row. */
const HEADER_SEPARATOR = ' · ';

/** Rail sits this close beneath the header row, so the two read as one group. */
const HEADER_TO_RAIL_GAP = '8px';

/**
 * True while the heading's text is wider than the room it has. Measured on
 * mount, on resize and whenever the text changes, so the tooltip only offers
 * the full row when the visible one is actually cut short.
 */
const useIsTruncated = (text: string): [React.RefObject<HTMLHeadingElement>, boolean] => {
  const ref = React.useRef<HTMLHeadingElement>(null);
  const [truncated, setTruncated] = React.useState(false);

  React.useLayoutEffect(() => {
    const measure = () => {
      const el = ref.current;
      setTruncated(!!el && el.scrollWidth > el.clientWidth);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [text]);

  return [ref, truncated];
};

const MaestroWorkflowStepper: React.FC<MaestroWorkflowStepperProps> = ({
  steps,
  title = 'Maestro',
  activeIndex,
  runningIndex = -1,
  onStepSelect,
}) => {
  const safeIndex = Math.max(0, Math.min(activeIndex, Math.max(steps.length - 1, 0)));
  const viewed: WorkflowStepperItem | undefined = steps[safeIndex];
  const agentName = viewed ? stepAgentName(viewed, safeIndex) : '';
  const headerText = `${title}${HEADER_SEPARATOR}${agentName}`;
  const [headingRef, truncated] = useIsTruncated(headerText);

  if (!viewed) return null;

  return (
    <Box data-testid="maestro-workflow-stepper">
      {/*
        One header row above the dots (ORCH-426): the panel title in bold, then
        "Step N: Name Agent" in regular weight and a step smaller, following the
        viewed step. It stays an `h6` so it sits level with the "Create Media
        Plan" heading in the left column (ORCH-257). Below `sm`, where the rail
        is hidden, this row alone identifies the step. It never wraps: a row too
        long for the panel is ellipsised and offers the full text as a tooltip.
      */}
      <Box data-testid="maestro-step-header" sx={{ minWidth: 0, mb: HEADER_TO_RAIL_GAP }}>
        <Tooltip title={truncated ? headerText : ''} disableInteractive>
          <Typography
            ref={headingRef}
            variant="h6"
            component="h6"
            noWrap
            data-testid="maestro-step-heading"
            aria-label={headerText}
            sx={{ fontWeight: 700, minWidth: 0 }}
          >
            {title}
            <Box
              component="span"
              sx={{ fontWeight: 400, color: 'text.secondary', whiteSpace: 'pre' }}
            >
              {HEADER_SEPARATOR}
            </Box>
            <Box
              component="span"
              data-testid="maestro-step-agent"
              sx={{ fontWeight: 400, fontSize: '0.875em' }}
            >
              {agentName}
            </Box>
          </Typography>
        </Tooltip>
      </Box>

      {/*
        Connectors flex, dots don't — so the rail fits any container width
        without clipping or scrolling, whatever the step count. Below `sm` it is
        hidden entirely and the header above carries position on its own
        (ORCH-277): a truncated or side-scrolling rail would reintroduce exactly
        the overflow defect this ticket removes from the field rail.
      */}
      <Box
        role="tablist"
        aria-label="Workflow steps"
        data-testid="maestro-step-rail"
        sx={{
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
        }}
      >
        {steps.map((step, idx) => {
          const state = dotState(step, idx, safeIndex, runningIndex);
          const isViewed = idx === safeIndex;
          const selectable = !step.locked && !!onStepSelect;
          const name = stepName(step, idx);
          // The connector leading into a reached step reads as travelled.
          const nextReached = idx < steps.length - 1 && !steps[idx + 1].locked;

          return (
            <React.Fragment key={step.key}>
              {/*
                Unreachable dots are `aria-disabled` rather than `disabled`: a
                disabled button takes no focus and fires no mouse events, so its
                tooltip could never open, from the keyboard or otherwise.

                No enter delay and no hover hysteresis: the name is the whole
                point of hovering a dot, so it appears the moment the pointer
                lands rather than after MUI's default pause (ORCH-407 Biz Review).
              */}
              <Tooltip
                title={name}
                enterDelay={0}
                enterNextDelay={0}
                enterTouchDelay={0}
                disableInteractive
              >
                <Box
                  component="button"
                  type="button"
                  role="tab"
                  aria-selected={isViewed}
                  aria-label={name}
                  aria-current={idx === runningIndex ? 'step' : undefined}
                  aria-disabled={!selectable}
                  data-testid="maestro-step-dot"
                  data-step-key={step.key}
                  data-step-label={step.label}
                  data-state={state}
                  onClick={selectable ? () => onStepSelect?.(idx) : undefined}
                  sx={{
                    flexShrink: 0,
                    p: 0,
                    m: 0,
                    appearance: 'none',
                    background: 'none',
                    border: 'none',
                    lineHeight: 0,
                    cursor: selectable ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 1,
                    },
                    '&:hover > span': selectable ? { transform: 'scale(1.15)' } : undefined,
                  }}
                >
                  <Box
                    component="span"
                    sx={(theme) => ({
                      display: 'block',
                      width: isViewed ? 11 : 8,
                      height: isViewed ? 11 : 8,
                      borderRadius: '50%',
                      transition: 'transform 0.15s, width 0.15s, height 0.15s',
                      bgcolor: isHollow(state) ? 'transparent' : theme.palette.primary.main,
                      border: isHollow(state) ? '1.5px solid' : 'none',
                      borderColor: 'divider',
                      // The viewed step gets a ring, but a locked one rings in
                      // grey: before a run every step is locked, and a teal ring
                      // on step 1 would read as "already running" (ORCH-273).
                      boxShadow: isViewed
                        ? `0 0 0 3px ${alpha(
                            isHollow(state)
                              ? theme.palette.text.disabled
                              : theme.palette.primary.main,
                            0.18
                          )}`
                        : 'none',
                      ...(state === 'running'
                        ? {
                            animation: 'maestroStepPulse 1.4s ease-in-out infinite',
                            '@keyframes maestroStepPulse': {
                              '0%, 100%': {
                                boxShadow: `0 0 0 0 ${alpha(theme.palette.primary.main, 0.35)}`,
                              },
                              '50%': {
                                boxShadow: `0 0 0 5px ${alpha(theme.palette.primary.main, 0)}`,
                              },
                            },
                          }
                        : {}),
                    })}
                  />
                </Box>
              </Tooltip>
              {idx < steps.length - 1 ? (
                <Box
                  aria-hidden
                  sx={{
                    flex: 1,
                    minWidth: 4,
                    height: 2,
                    borderRadius: 1,
                    bgcolor: step.completed && nextReached ? 'primary.main' : 'divider',
                  }}
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export default MaestroWorkflowStepper;
